/**
 * NotificationDropdown Component
 *
 * Dropdown component for displaying real-time notifications using SSE.
 * Features: unread badge, grouping by type, mark as read, empty state.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  type Notification,
  formatNotificationTime,
  getNotificationColor,
  getNotificationCategory,
  type SSEConnectionState,
} from '@/services/notifications';
import { useNotifications } from '@/hooks/useNotifications';
import styles from './NotificationDropdown.module.css';

// ============================================================================
// Types
// ============================================================================

type NotificationGroup = {
  category: string;
  label: string;
  notifications: Notification[];
};

// ============================================================================
// Helper Functions
// ============================================================================

function getCategoryLabel(category: string): string {
  switch (category) {
    case 'workflow':
      return 'Workflow';
    case 'records':
      return 'Records';
    case 'compliance':
      return 'Compliance';
    case 'security':
      return 'Security';
    case 'system':
      return 'System';
    default:
      return 'Other';
  }
}

function groupNotificationsByCategory(notifications: Notification[]): NotificationGroup[] {
  const groups: Record<string, Notification[]> = {};

  notifications.forEach((notification) => {
    const category = getNotificationCategory(notification.type);
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(notification);
  });

  // Sort categories by priority
  const categoryOrder = ['security', 'compliance', 'workflow', 'records', 'system'];

  return categoryOrder
    .filter((category) => groups[category]?.length > 0)
    .map((category) => ({
      category,
      label: getCategoryLabel(category),
      notifications: groups[category],
    }));
}

// ============================================================================
// Connection Status Badge
// ============================================================================

function ConnectionStatusBadge({ state }: { state: SSEConnectionState }) {
  if (state === 'connected') return null;

  const statusConfig = {
    disconnected: { color: 'var(--error)', label: 'Offline' },
    connecting: { color: 'var(--warning)', label: 'Connecting...' },
    reconnecting: { color: 'var(--warning)', label: 'Reconnecting...' },
  };

  const config = statusConfig[state];

  return (
    <div className={styles.connectionStatus} style={{ color: config.color }}>
      <span className={styles.connectionDot} style={{ backgroundColor: config.color }} />
      {config.label}
    </div>
  );
}

// ============================================================================
// Notification Item Component
// ============================================================================

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string, event: React.MouseEvent) => void;
  onClose: () => void;
}

function NotificationItem({ notification, onMarkAsRead, onClose }: NotificationItemProps) {
  const link = getNotificationLink(notification);

  const content = (
    <>
      <div
        className={styles.notificationIcon}
        style={{ color: getNotificationColor(notification.type) }}
      >
        {renderNotificationIcon(notification.type)}
      </div>
      <div className={styles.notificationContent}>
        <p className={styles.notificationTitle}>{notification.title}</p>
        <p className={styles.notificationMessage}>{notification.message}</p>
        <span className={styles.notificationTime}>
          {formatNotificationTime(notification.createdAt)}
        </span>
      </div>
      {!notification.isRead && (
        <button
          className={styles.markRead}
          onClick={(e) => onMarkAsRead(notification.id, e)}
          aria-label="Mark as read"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </button>
      )}
    </>
  );

  if (link) {
    return (
      <Link
        to={link}
        className={`${styles.notificationItem} ${!notification.isRead ? styles.unread : ''}`}
        onClick={onClose}
      >
        {content}
      </Link>
    );
  }

  return (
    <div
      className={`${styles.notificationItem} ${!notification.isRead ? styles.unread : ''}`}
    >
      {content}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [groupByType, setGroupByType] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    connectionState,
    isLoading,
    markAsRead,
    markAllAsRead,
    refresh,
  } = useNotifications({
    autoConnect: true,
    showToasts: true,
    toastPriorityThreshold: 'medium',
  });

  // Get recent notifications (limit to 10 for dropdown)
  const recentNotifications = useMemo(
    () => notifications.slice(0, 10),
    [notifications]
  );

  // Group notifications by category if grouping is enabled
  const groupedNotifications = useMemo(
    () => (groupByType ? groupNotificationsByCategory(recentNotifications) : null),
    [recentNotifications, groupByType]
  );

  // Refresh when dropdown opens
  useEffect(() => {
    if (isOpen) {
      refresh();
    }
  }, [isOpen, refresh]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleMarkAsRead = useCallback(
    async (id: string, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      try {
        await markAsRead(id);
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    },
    [markAsRead]
  );

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsRead();
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  }, [markAllAsRead]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <div className={styles.container} ref={dropdownRef}>
      <button
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className={styles.badge} aria-hidden="true">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {connectionState !== 'connected' && connectionState !== 'disconnected' && (
          <span className={styles.connectingIndicator} aria-hidden="true" />
        )}
      </button>

      {isOpen && (
        <div className={styles.dropdown} role="menu" aria-label="Notifications">
          <div className={styles.header}>
            <h3 className={styles.title}>Notifications</h3>
            <div className={styles.headerActions}>
              <button
                className={styles.groupToggle}
                onClick={() => setGroupByType(!groupByType)}
                aria-pressed={groupByType}
                title={groupByType ? 'Show chronologically' : 'Group by type'}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  {groupByType ? (
                    <>
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </>
                  ) : (
                    <>
                      <rect x="3" y="3" width="7" height="7" />
                      <rect x="14" y="3" width="7" height="7" />
                      <rect x="14" y="14" width="7" height="7" />
                      <rect x="3" y="14" width="7" height="7" />
                    </>
                  )}
                </svg>
              </button>
              {unreadCount > 0 && (
                <button className={styles.markAllRead} onClick={handleMarkAllAsRead}>
                  Mark all as read
                </button>
              )}
            </div>
          </div>

          <ConnectionStatusBadge state={connectionState} />

          <div className={styles.list}>
            {isLoading ? (
              <div className={styles.loading}>
                <div className={styles.loadingSpinner} />
                Loading notifications...
              </div>
            ) : recentNotifications.length === 0 ? (
              <div className={styles.empty}>
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <p>No notifications yet</p>
                <span className={styles.emptySubtext}>
                  You're all caught up!
                </span>
              </div>
            ) : groupedNotifications ? (
              // Grouped view
              groupedNotifications.map((group) => (
                <div key={group.category} className={styles.notificationGroup}>
                  <div className={styles.groupHeader}>
                    <span className={styles.groupLabel}>{group.label}</span>
                    <span className={styles.groupCount}>{group.notifications.length}</span>
                  </div>
                  {group.notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onMarkAsRead={handleMarkAsRead}
                      onClose={handleClose}
                    />
                  ))}
                </div>
              ))
            ) : (
              // Chronological view
              recentNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onClose={handleClose}
                />
              ))
            )}
          </div>

          <Link
            to="/notifications"
            className={styles.viewAll}
            onClick={() => setIsOpen(false)}
          >
            View all notifications
          </Link>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

function getNotificationLink(notification: Notification): string | null {
  const data = notification.data;
  if (!data) return null;

  switch (notification.type) {
    case 'workflow_assigned':
    case 'workflow_delegated':
    case 'task_due':
      return '/tasks';
    case 'workflow_approved':
    case 'workflow_rejected':
    case 'record_created':
    case 'record_updated':
      return data.documentId ? `/records?doc=${data.documentId}` : '/records';
    case 'compliance_retention_due':
    case 'compliance_review_required':
    case 'compliance_hold_placed':
    case 'compliance_hold_released':
      return '/compliance';
    case 'security_access_violation':
    case 'security_suspicious_activity':
    case 'security_login_failed':
    case 'security_permission_changed':
    case 'anomaly_detected':
    case 'anomaly_resolved':
      return '/security/audit-logs';
    default:
      return null;
  }
}

function renderNotificationIcon(type: string) {
  switch (type) {
    case 'workflow_assigned':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
        </svg>
      );
    case 'workflow_approved':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      );
    case 'workflow_rejected':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      );
    case 'workflow_delegated':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'record_created':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="12" y1="18" x2="12" y2="12" />
          <line x1="9" y1="15" x2="15" y2="15" />
        </svg>
      );
    case 'record_updated':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M16 13H8" />
          <path d="M16 17H8" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      );
    case 'task_due':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      );
    case 'system_announcement':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
        </svg>
      );
    case 'compliance_retention_due':
    case 'compliance_review_required':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      );
    case 'compliance_hold_placed':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      );
    case 'compliance_hold_released':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 9.9-1" />
        </svg>
      );
    case 'security_access_violation':
    case 'security_suspicious_activity':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      );
    case 'security_login_failed':
    case 'security_permission_changed':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
        </svg>
      );
    case 'anomaly_detected':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      );
    case 'anomaly_resolved':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      );
    default:
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      );
  }
}
